import {
  Avatar,
  Box,
  Button,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { CaseCreationFormArr } from "./Data";
import { DropdownArrPropsType, FormDataType } from "DataTypes/Form";
import { commonFormSwitch, ControlledBoxSizeSwitch } from "Utils/SwitchCases";
import {
  FormatDropdownData,
  HandleFormChange,
  useSavedData,
  ValidateFormData,
} from "Utils/Common";
import ImgPath from "Constants/Imgs";
import { UserInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import { Form, useNavigate } from "react-router-dom";
import { RouteUrls } from "Constants/RouteUrls";
import CaseApi from "Service/Case.api";
import { GlobalResType, HandleChangeProps } from "DataTypes/Common";
import { useDispatch } from "react-redux";
import {
  setAlertData,
  startLoading,
  stopLoading,
} from "../../../../../Redux/Screen/Action";
import ControlledForm from "Components/ControlledForm";
import ModalLayout from "Layouts/Modal";

const CaseCreation: FC = () => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState<FormDataType[]>(CaseCreationFormArr);
  const [bulkUpload, setBulkUpload] = useState<boolean>(false);
  const [errorObj, setErrorObj] = useState({
    csvError: false,
    csvErrorText: "",
    zipError: false,
    zipErrorText: "",
  });
  const [caseResponse, setCaseResponse] = useState<any>([]);
  const [selectedCSVFile, setSelectedCSVFile] = useState<File | null>(null);
  const [selectedZip, setSelectedZip] = useState<File | null>(null);
  const [consentGiven, setConsentGiven] = useState<"yes" | "no">("yes");
  const [isUploadSuccess, setIsUploadSuccess] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const navigate = useNavigate();

  useEffect(() => {
    handleOnLoad();
    // ----------------------------------------------------
    //  NEW: Cleanup Function (runs when component unmounts)
    // ----------------------------------------------------
    // return () => {
        // If the component is unmounting (user navigated away or closed the tab), 
        // clear the stored selection data.
        if (localStorage.getItem("userPurchaseData")) {
            localStorage.removeItem("userPurchaseData");
            console.log("Cleanup: selectedData cleared because CaseCreation was abandoned.");
        }
    // };
  }, []);
  const getSavedData = useSavedData();

  const handleOnLoad: () => void = async () => {
    dispatch(startLoading({ loading: true, loadtext: "Loading..." }));
    try {
      const OrderTypeDrop: DropdownArrPropsType[] = await getOrderTypes();

      const res: GlobalResType = await CaseApi.fetchCaseTypes();

      let CaseTypeDrop: DropdownArrPropsType[] = [];
      console.log(res.data, "res.data");

      if (res?.success) {
        setCaseResponse(res.data);
      }

      let orderTypeId: number | null = getSavedData()?.orderTypeId;
      let caseTypeId: number | null = getSavedData()?.caseTypeId;

      if (orderTypeId && res?.success) {
        const allCaseTypes = res.data; // use API data directly
        const filterArr = allCaseTypes.filter(
          (el: any) => el.orderTypeId === orderTypeId
        );
        CaseTypeDrop = FormatDropdownData(filterArr, "id", "name", "id");
      }

      setFormData((prev) =>
        prev.map((el) => {
          // skip description field
          if (el.id === "description") {
            return el;
          }
          if (el.id === "orderType") {
            const selectedOrder = OrderTypeDrop.find(
              (opt) => opt.value === orderTypeId
            );

            return {
              ...el,
              dropArr: OrderTypeDrop,
              value: selectedOrder?.value ?? "",
              dropValue: selectedOrder?.value ?? "",
            };
          }

          if (el.id === "caseType") {
            const selectedCase = CaseTypeDrop.find(
              (opt) => opt.value === caseTypeId
            );

            return {
              ...el,
              dropArr: CaseTypeDrop,
              value: selectedCase?.value ?? "",
              dropValue: selectedCase?.value ?? "",
            };
          }
          return el;
        })
      );
    } catch (error) {
      console.error("❌ Error in handleOnLoad:", error);
    } finally {
      dispatch(stopLoading());
    }
  };

  const getCaseTypes: (
    orderTypeId: number
  ) => Promise<DropdownArrPropsType[]> = async (orderTypeId) => {
    const filterArr = caseResponse.filter(
      (el: any) => el.orderTypeId === orderTypeId
    );
    return FormatDropdownData(filterArr, "id", "name", "id");
  };

  const getOrderTypes: () => Promise<DropdownArrPropsType[]> = async () => {
    const res: GlobalResType = await CaseApi.fetchOrderTypes();
    if (res?.success) {
      return FormatDropdownData(res?.data, "id", "name", "id");
    }
    return [];
  };

  const handleSubmit: () => void = async () => {
    const payload = {
      clientId: myInfo.id,
      caseTypeId: formData[1]?.value,
      orderTypeId: formData[0]?.value,
      description: formData[2]?.value || "",
    };
    const validation = ValidateFormData(formData);
    setFormData(validation?.formData);
    if (!validation?.errorCheck) {
      dispatch(startLoading({ loading: true, loadtext: "Loading..." }));
      const res = await CaseApi.createCase(payload);
      if (res?.success) {
        localStorage.removeItem("userPurchaseData");
        // handle here 0rs payment if amount is 0rs then not redirect to payment page
        if (res?.data?.casePrice === 0) {
          console.log(res?.data?.pkCase, "res?.data?.pkCase");
          navigate(`/chat/${res?.data?.orderTypeId}`);
          dispatch(stopLoading());
          return;
        }

        navigate(`${res?.data?.pkCase}/${RouteUrls.payment}`);
        dispatch(stopLoading());
      }
      dispatch(stopLoading());
    }
  };

  const handleChange: (data: HandleChangeProps) => void = async (data) => {
    const { id, value } = data;
    let newFormData: FormDataType[] = JSON.parse(JSON.stringify(formData));
    if (id === "orderType") {
      const orderTypeName = newFormData[0].dropArr?.find(
        (el) => el.value === value
      )?.label;

      if (
        orderTypeName === "Online Mediation" ||
        orderTypeName === "Online Arbitration"
      ) {
        setBulkUpload(true);
        if (newFormData.length > 1) {
          newFormData.splice(1, 2);
        }
      } else {
        setBulkUpload(false);
        if (newFormData.length === 1) {
          newFormData.push({ ...CaseCreationFormArr[1] });
          newFormData.push({ ...CaseCreationFormArr[2] });
        }
        const orderTypeId = typeof value === "number" ? value : 0;
        const caseTypeDrop = await getCaseTypes(orderTypeId);
        if (newFormData[1]) {
          newFormData[1].dropArr = caseTypeDrop;
          newFormData[1].value = "";
        }
        if (newFormData[2]) {
          newFormData[2].value = "";
        }
      }
    }
    const updatedFormData = HandleFormChange(data, newFormData);
    setFormData(updatedFormData);
  };

  const handleDocumentUpload: () => void = async () => {
    let errors = {
      csvError: false,
      csvErrorText: "",
      zipError: false,
      zipErrorText: "",
    };
    if (!selectedCSVFile) {
      errors.csvError = true;
      errors.csvErrorText = "Please select a CSV file";
    }

    if (!selectedZip) {
      errors.zipError = true;
      errors.zipErrorText = "Please select a ZIP file";
    }
    setErrorObj(errors);

    // If there are any errors, stop execution
    if (errors.csvError || errors.zipError) return;

    if (selectedCSVFile && selectedZip) {
      const multipartFormData = new FormData();
      multipartFormData.append("csvFile", selectedCSVFile);
      multipartFormData.append("zipFile", selectedZip);
      multipartFormData.append(
        "requestDto",
        JSON.stringify({
          orderTypeId: formData[0]?.value,
          description: "",
          csvFileName: selectedCSVFile?.name,
          zipFileName: selectedZip?.name,
          batchNumber: "",
          isBulkUpload: true,
          isConsentGivenToUs: consentGiven === "yes" ? true : false,
          clientId: myInfo?.id,
        })
      );
      dispatch(startLoading({ loading: true, loadtext: "Loading..." }));
      const res = await CaseApi.DocumentBulkUpload(multipartFormData);
      if (res?.success) {
        setIsUploadSuccess(true);
        // NEW: Remove the data after successful bulk upload and before navigation
        localStorage.removeItem("userPurchaseData");
        dispatch(
          setAlertData({
            alertMessage:
              "We have received your uploaded files, Support team will get back to you soon.",
            alertVariant: "success",
            openAlert: true,
          })
        );
        dispatch(stopLoading());
        navigate(`/${RouteUrls.dashboard}`);
      }
      dispatch(stopLoading());
    }
  };

  const handleFormValidation = () => {
    const validation = ValidateFormData(formData);
    setFormData(validation?.formData);
    if (validation?.errorCheck === false) {
      setModalOpen(true);
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 4,
      }}
    >
      <ModalLayout
        content={
          <Typography>Are you sure want to create a new case?</Typography>
        }
        open={modalOpen}
        modwidth="400px"
        onClose={() => setModalOpen(false)}
        heading="New Case"
        buttonArr={[
          {
            id: "createCase",
            label: "Create Case",
            clickHandler: bulkUpload ? handleDocumentUpload : handleSubmit,
          },
          {
            id: "cancel",
            label: "Cancel",
            clickHandler: () => setModalOpen(false),
            type: "border",
          },
        ]}
      />
      <Avatar src={ImgPath.FileCaseIcon} sx={{ m: 1 }}></Avatar>
      <Typography
        component="h1"
        color="primary.main"
        variant="h5"
        sx={{ mb: 2 }}
      >
        Case Creation
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          gap: { xs: 2, md: 4 },
          px: { xs: 2, md: 0 },
          maxWidth: { xs: "100%" },
          mx: "auto",
        }}
      >
        <Box
          sx={{
            mx: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            flex: "1 1 65%",
            maxWidth: { xs: "100%", md: "65%" },
          }}
        >
          {/* <Avatar src={ImgPath.FileCaseIcon} sx={{ m: 1 }}></Avatar>
          <Typography
            component="h1"
            color="primary.main"
            variant="h5"
            sx={{ mb: 2 }}
          >
            Case Creation
          </Typography> */}

          <Grid
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              width: { xs: "100%", md: "60%" },
            }}
            container
            spacing={2}
            justifyContent="center"
          >
            {formData.map((el: FormDataType) => (
              <Grid key={el.id} {...ControlledBoxSizeSwitch(el.boxSize)}>
                {commonFormSwitch({
                  formObj: el,
                  onChange: (data) => handleChange(data),
                })}
              </Grid>
            ))}
          </Grid>

          {bulkUpload && (
            <Grid
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                width: { xs: "100%", md: "60%" },
                mt: 1,
              }}
              container
              spacing={2}
              justifyContent="center"
            >
              <Grid size={{ md: 12, lg: 12 }}>
                <ControlledForm.Input
                  id="csvSelection"
                  type="file"
                  onChange={(value) =>
                    setSelectedCSVFile(
                      typeof value == "object" ? value[0] : null
                    )
                  }
                  acceptFileTypes="text/csv"
                  required={true}
                  label="Upload CSV Document"
                  focused={true}
                  readOnly={isUploadSuccess}
                  error={errorObj.csvError}
                  errorText={errorObj.csvErrorText}
                />
                <Box mt={2}>
                  <ControlledForm.Input
                    id="zipSelection"
                    type="file"
                    acceptFileTypes="application/zip"
                    required={true}
                    onChange={(value) =>
                      setSelectedZip(typeof value == "object" ? value[0] : null)
                    }
                    label="Upload ZIP File"
                    focused={true}
                    readOnly={isUploadSuccess}
                    error={errorObj.zipError}
                    errorText={errorObj.zipErrorText}
                  />
                </Box>
                {/* <Box mt={2} textAlign="center">
                <Button
                  variant="contained"
                  disabled={isUploadSuccess}
                  onClick={handleDocumentUpload}
                >
                  {isUploadSuccess ? "Upload Successfull" : "Upload"}
                </Button>
              </Box> */}
                <Typography
                  align="center" // centers the text
                  mb={1}
                  mt={1}
                >
                  Do you have the Consent to file with us?
                </Typography>
                <Box
                  mt={2}
                  textAlign="center"
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <RadioGroup
                    aria-labelledby="demo-radio-buttons-group-label"
                    name="radio-buttons-group"
                    value={consentGiven}
                    onChange={(e) => setConsentGiven(e.target.value as any)}
                    sx={{
                      alignContent: "center",
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <FormControlLabel
                      disabled={isUploadSuccess}
                      value="yes"
                      control={<Radio sx={sxRadio} />}
                      label="Yes"
                    />
                    <FormControlLabel
                      disabled={isUploadSuccess}
                      value="no"
                      control={<Radio sx={sxRadio} />}
                      label="No"
                    />
                  </RadioGroup>
                </Box>
              </Grid>
            </Grid>
          )}

          <Box mt={3}>
            <Button
              variant="contained"
              // onClick={bulkUpload ? handleDocumentUpload : handleSubmit}
              onClick={handleFormValidation}
            >
              Submit
            </Button>
          </Box>
        </Box>

        <Box
          className="summaryContainer"
          sx={{
            width: { xs: "100%", md: "30%" },
            ml: { xs: 0, md: 2 },
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          {/* Order Summary Card */}
          <Box
            sx={{
              width: "100%",
              border: "1px solid rgba(0,0,0,0.12)",
              borderRadius: 2,
              p: 2,
              bgcolor: "background.paper",
              position: { xs: "static", md: "sticky" },
              top: { md: 120 },
            }}
          >
            <Typography
              sx={{ textAlign: "center" }}
              variant="h6"
              color="text.primary"
              gutterBottom
            >
              Order Summary
            </Typography>

            {/* compute labels and amounts from formData / caseResponse */}
            {(() => {
              const orderVal = formData?.[0]?.value;
              const orderTypeLabel = formData?.[0]?.dropArr?.find((d: any) => d.value === orderVal)?.label ?? "--";

              const caseVal = formData?.[1]?.value;
              const caseTypeLabel = formData?.[1]?.dropArr?.find((d: any) => d.value === caseVal)?.label ?? "--";

              let baseAmount = 0;
              let gstAmount = 0;
              let finalAmount = 0;

              if (caseVal) {
                const matched = caseResponse?.find((c: any) => String(c?.id) === String(caseVal));
                if (matched) {
                  baseAmount = matched.casePrice || 0;
                  if (baseAmount > 0) {
                    gstAmount = baseAmount * 0.18;
                    finalAmount = baseAmount + gstAmount;
                  } else {
                    finalAmount = baseAmount;
                  }
                }
              }

              return (
                <Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography>Order Type:</Typography>
                    <Typography fontWeight="500">{orderTypeLabel}</Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography>Case Type:</Typography>
                    <Typography fontWeight="500">{caseTypeLabel}</Typography>
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                    <Typography>Base Price:</Typography>
                    <Typography>{baseAmount > 0 ? `₹${baseAmount.toLocaleString()}` : "--"}</Typography>
                  </Box>

                  {/* Only show GST if price is greater than 0 */}
                  {baseAmount > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Typography>GST (18%):</Typography>
                      <Typography>₹{gstAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
                    </Box>
                  )}

                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2, pt: 1, borderTop: "1px solid #eee" }}>
                    <Typography variant="subtitle1" fontWeight="bold">Total Amount:</Typography>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary.main">
                      {finalAmount > 0 ? `₹${finalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "Free"}
                    </Typography>
                  </Box>
                </Box>
              );
            })()}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CaseCreation;

const sxRadio = {
  color: "#a24857", // default outline color
  "&.Mui-checked": {
    color: "#a24857", // fill when selected
  },
  "&:hover": {
    backgroundColor: "rgba(128,0,0,0.08)", // maroon hover effect
  },
};
